import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Alert, Box } from "@mui/material";
import { useTranslation } from "react-i18next";
import BarChartOutlined from "@mui/icons-material/BarChartOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import AltRouteIcon from "@mui/icons-material/AltRoute";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import SpeedIcon from "@mui/icons-material/Speed";
import config from "../../config.js";
import { usePermission } from "../../hooks/usePermission";
import { useUser } from "../../pages/login/UserContextPersistent";
import InactiveInventoryTable from "../common/InactiveInventoryTable";
import { PAGE_IDS, isJsonDrivenLayoutEnabled } from "../../config/uiLayoutConfig";
import { usePageLayout } from "../../context/LayoutContext";
import { getBundledLayout } from "../../services/uiLayoutService";
import {
  FLEET_ROUTE_ROW_ACTIONS,
  ROUTE_DASHBOARD_STAT_METRIC_KEYS,
  ROUTE_STAT_TONE_BY_VARIANT,
  computeFleetRouteMetric,
} from "../../config/routesModuleCatalog";
import { hasLayoutPermission } from "../../config/vehiclesModuleCatalog";
import {
  buildFleetRowActionsFromLayout,
  getActionBarWidgets,
  getLayoutSection,
  getPageTitleKey,
} from "../../layout/moduleLayoutUtils";
import { reportsApi, getOrganizationId } from "../manageData/reportsApi.js";
import DeleteModal from "../common/DeleteModal";
import SubTitleNew from "../common/New/SubTitleNew";
import StatsCard from "../common/New/StatsCard.jsx";
import DynamicTable, {
  TableLastUpdate,
  TableStatusBadge,
} from "../common/New/DynamicTable";
import RouteAddMain from "./RouteAddMain";
import {
  CUSTOM_DATE_OPTION,
  DASHBOARD_DATE_OPTIONS,
  getReportRows,
} from "../vehicle/DashboardGraph";
import RouteDashboardGraph from "./RouteDashboardGraph";
import {
  EMPTY_ROUTE_METRICS,
  buildRouteMetrics,
  computePercentChangeTrend,
  formatDistanceLabel,
  formatDurationLabel,
  formatDateInput,
  getComparisonPeriodParams,
  getPeriodBounds,
  getRouteAdherencePercent,
  getRouteDistanceKm,
  getRouteDurationMinutes,
  getRouteLastUpdateValue,
  getRouteOperationalStatus,
  getRouteStopCount,
  getStopCorridor,
  getVehicleNames,
  toReportDateOption,
  buildRouteFullData,
} from "./routeDashboardUtils";
import { formatLastUpdate } from "../../utils/formatLastUpdate";
import { downloadBackendExcel, exportTableToCSV } from "../../utils/exportTableData";
import {
  clearCreateIntent,
  readCreateIntent,
} from "../../utils/moduleCreateIntent";
import "../vehicle/Vehicle.css";
import "./Route.css";

const ROUTE_DASHBOARD_REVALIDATE_MS = 60 * 1000;
const routeDashboardReportCache = new Map();
const routeDashboardInflight = new Map();
const routeDeviationCache = new Map();
const routeDeviationInflight = new Map();
const routeListCache = new Map();
let routeListRequestId = 0;

const startOfToday = () => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return today;
};

const readStoredOrgId = () => {
  try {
    return JSON.parse(localStorage.getItem("userState"))?.user?.organizationId || "";
  } catch {
    return "";
  }
};

const routeReportCacheKey = (orgId, dateOption, customRange = {}) =>
  [
    orgId || "",
    formatDateInput(new Date()),
    toReportDateOption(dateOption),
    customRange.start || "",
    customRange.end || "",
  ].join("|");

const periodIncludesToday = (dateOption, customRange = {}) => {
  const { start, end } = getPeriodBounds(dateOption, customRange);
  if (Number.isNaN(start?.getTime?.()) || Number.isNaN(end?.getTime?.())) return true;
  const today = startOfToday();
  const startDay = new Date(start);
  startDay.setHours(0, 0, 0, 0);
  const endDay = new Date(end);
  endDay.setHours(0, 0, 0, 0);
  return startDay <= today && endDay >= today;
};

const isComparisonSettled = (comparisonPeriod) => {
  const option = toReportDateOption(comparisonPeriod?.dateOption);
  if (option === "yesterday") return true;
  if (option !== "custom" || !comparisonPeriod?.customEndDate) return false;
  const end = new Date(`${comparisonPeriod.customEndDate}T00:00:00`);
  return !Number.isNaN(end.getTime()) && end < startOfToday();
};

const readCachedRouteDashboard = (orgId, dateOption, customRange) =>
  routeDashboardReportCache.get(routeReportCacheKey(orgId, dateOption, customRange)) || null;

const routeDeviationCacheKey = (orgId, dateOption = "today", customRange = {}) =>
  [
    orgId || "",
    formatDateInput(new Date()),
    toReportDateOption(dateOption),
    customRange.start || "",
    customRange.end || "",
    "deviation",
  ].join("|");

const readRouteListCache = (orgId) => routeListCache.get(String(orgId || "")) || null;

const shareInflight = (inflight, key, load) => {
  const existing = inflight.get(key);
  if (existing) return existing;
  const promise = Promise.resolve()
    .then(load)
    .finally(() => {
      if (inflight.get(key) === promise) inflight.delete(key);
    });
  inflight.set(key, promise);
  return promise;
};

const Route = ({ variant } = {}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const isSchool =
    variant === "school" || location.pathname.startsWith("/school-routes");
  const { hasPermission } = usePermission();
  const { organizationInactive } = useUser();
  const useJsonLayout = isJsonDrivenLayoutEnabled();
  const layoutPageId = useJsonLayout
    ? isSchool
      ? PAGE_IDS.SCHOOL_ROUTES
      : PAGE_IDS.FLEET_ROUTES
    : null;
  const { layout: moduleLayout } = usePageLayout(layoutPageId);
  const bundledLayout = layoutPageId ? getBundledLayout(layoutPageId) : null;

  const [routeData, setRouteData] = useState(
    () => readRouteListCache(readStoredOrgId())?.routes || [],
  );
  const [isLoading, setIsLoading] = useState(
    () => !readRouteListCache(readStoredOrgId()),
  );
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [routeFilter, setRouteFilter] = useState("");
  const [quickFilter, setQuickFilter] = useState("");
  const [sortBy, setSortBy] = useState("Newest First");
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [routeToDelete, setRouteToDelete] = useState(null);
  const [dashboardDateOption, setDashboardDateOption] = useState("today");
  const [dashboardCustomRange, setDashboardCustomRange] = useState({
    start: "",
    end: "",
  });
  const [dashboardError, setDashboardError] = useState("");
  const [tripRows, setTripRows] = useState(
    () => readCachedRouteDashboard(readStoredOrgId(), "today", { start: "", end: "" })?.tripRows || [],
  );
  const [previousTripRows, setPreviousTripRows] = useState(
    () =>
      readCachedRouteDashboard(readStoredOrgId(), "today", { start: "", end: "" })
        ?.previousTripRows || [],
  );
  const [runtimeRows, setRuntimeRows] = useState(
    () =>
      readCachedRouteDashboard(readStoredOrgId(), "today", { start: "", end: "" })?.runtimeRows ||
      [],
  );
  const [previousRuntimeRows, setPreviousRuntimeRows] = useState(
    () =>
      readCachedRouteDashboard(readStoredOrgId(), "today", { start: "", end: "" })
        ?.previousRuntimeRows || [],
  );
  const [deviationRows, setDeviationRows] = useState(
    () => routeDeviationCache.get(routeDeviationCacheKey(readStoredOrgId(), "today"))?.rows || [],
  );
  const [isMetricsLoading, setIsMetricsLoading] = useState(
    () => !readCachedRouteDashboard(readStoredOrgId(), "today", { start: "", end: "" }),
  );
  const [comparisonReady, setComparisonReady] = useState(() => {
    const cached = readCachedRouteDashboard(readStoredOrgId(), "today", { start: "", end: "" });
    return Boolean(cached) && cached.comparisonReady !== false;
  });
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [expandedRouteId, setExpandedRouteId] = useState(null);
  const [selectedRouteIds, setSelectedRouteIds] = useState([]);
  const tableSectionRef = useRef(null);

  const parsedData = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("userState") || "null");
    } catch {
      return null;
    }
  }, []);
  const orgId = parsedData?.user?.organizationId;
  const orgName = parsedData?.organization?.name;

  const layoutRowActions = useMemo(() => {
    if (!moduleLayout) return null;
    return buildFleetRowActionsFromLayout(
      moduleLayout,
      { hasPermission },
      FLEET_ROUTE_ROW_ACTIONS,
    );
  }, [moduleLayout, hasPermission]);

  const actionBarWidgets = useMemo(
    () => (moduleLayout ? getActionBarWidgets(moduleLayout, hasPermission) : []),
    [moduleLayout, hasPermission],
  );

  const getCustomActions = () =>
    Object.values(FLEET_ROUTE_ROW_ACTIONS).filter((action) =>
      hasLayoutPermission(action.permission, hasPermission),
    );

  const tableActions = layoutRowActions || getCustomActions();
  const canCreateRoute = (() => {
    const createAction = actionBarWidgets.find(
      (widget) => widget.actionKey === "createRoute",
    );
    return createAction
      ? hasLayoutPermission(createAction.permission, hasPermission)
      : hasPermission("/api/route", "POST");
  })();

  useEffect(() => {
    const { openCreate, expandId } = readCreateIntent(location);
    if (!openCreate && !expandId) return;
    if (openCreate && canCreateRoute) {
      setExpandedRouteId(null);
      setShowCreateForm(true);
    } else if (expandId) {
      setShowCreateForm(false);
      setExpandedRouteId(expandId);
    }
    clearCreateIntent(navigate, location);
  }, [canCreateRoute, location, navigate]);

  const canUpdateRoute =
    tableActions.some((action) => action.id === "update") ||
    hasPermission("/api/route", "PUT");
  const canMonitorRoute =
    tableActions.some((action) => action.id === "monitor") || isSchool;
  const createAction = actionBarWidgets.find(
    (widget) => widget.actionKey === "createRoute",
  );
  const monitorAction = actionBarWidgets.find(
    (widget) => widget.actionKey === "monitorRoutes",
  );
  const pageTitle = getPageTitleKey(moduleLayout) || "routes";

  const fetchRoutes = useCallback(async (force = false) => {
    const resolvedOrgId = orgId || readStoredOrgId();
    const cacheKey = String(resolvedOrgId || "");
    if (force) routeListCache.delete(cacheKey);
    const cached = routeListCache.get(cacheKey);
    const cacheIsFresh =
      cached && Date.now() - cached.fetchedAt < ROUTE_DASHBOARD_REVALIDATE_MS;

    if (cached) {
      setRouteData(cached.routes);
      setIsLoading(false);
      if (isSchool) {
        localStorage.setItem(
          "schoolRouteMonitor:allRoutes",
          JSON.stringify(cached.routes),
        );
      }
      if (cacheIsFresh) return;
    } else {
      setIsLoading(true);
    }

    const requestId = ++routeListRequestId;

    try {
      const [routesResponse, stopsResponse] = await Promise.all([
        axios.get(`${config.baseURL}/route/organization`, {
          params: { organizationId: resolvedOrgId },
          withCredentials: true,
        }),
        axios.get(`${config.baseURL}/stop/organization`, {
          withCredentials: true,
        }),
      ]);

      if (requestId !== routeListRequestId) return;

      if (routesResponse.status === 401) {
        navigate("/");
        return;
      }

      const allOrgStops = Array.isArray(stopsResponse.data)
        ? stopsResponse.data
        : [];
      const routes = Array.isArray(routesResponse.data) ? routesResponse.data : [];
      const routesWithStops = routes.map((route) =>
        buildRouteFullData(route, allOrgStops),
      );

      if (requestId !== routeListRequestId) return;

      routeListCache.set(cacheKey, {
        routes: routesWithStops,
        fetchedAt: Date.now(),
      });

      if (isSchool) {
        localStorage.setItem(
          "schoolRouteMonitor:allRoutes",
          JSON.stringify(routesWithStops),
        );
      }

      setRouteData(routesWithStops);
    } catch (error) {
      if (requestId !== routeListRequestId) return;
      if (error.response?.status === 401) {
        navigate("/");
        return;
      }
      console.log("Error in fetching routes by organization", error);
    } finally {
      if (requestId === routeListRequestId) setIsLoading(false);
    }
  }, [isSchool, navigate, orgId]);

  useEffect(() => {
    fetchRoutes(false);
  }, [fetchRoutes]);

  const handleDashboardDateRangeChange = useCallback((range) => {
    setDashboardError("");
    if (range?.type === "custom") {
      setDashboardDateOption(CUSTOM_DATE_OPTION);
      setDashboardCustomRange({
        start: range.startDate || "",
        end: range.endDate || "",
      });
      return;
    }
    setDashboardDateOption(toReportDateOption(range?.value || range?.label));
    setDashboardCustomRange({ start: "", end: "" });
  }, []);

  useEffect(() => {
    let cancelled = false;
    const isCustom = dashboardDateOption === CUSTOM_DATE_OPTION;
    if (isCustom && !(dashboardCustomRange.start && dashboardCustomRange.end)) {
      setIsMetricsLoading(false);
      setComparisonReady(false);
      setTripRows([]);
      setRuntimeRows([]);
      setPreviousTripRows([]);
      setPreviousRuntimeRows([]);
      setDeviationRows([]);
      setDashboardError("");
      return undefined;
    }

    const resolvedOrgId = orgId || getOrganizationId() || "";
    const cacheKey = routeReportCacheKey(
      resolvedOrgId,
      dashboardDateOption,
      dashboardCustomRange,
    );
    const cached = routeDashboardReportCache.get(cacheKey) || null;
    const includesToday = periodIncludesToday(dashboardDateOption, dashboardCustomRange);
    const cacheIsFresh =
      cached && Date.now() - cached.fetchedAt < ROUTE_DASHBOARD_REVALIDATE_MS;
    const shouldRefetch = !cached || (includesToday && !cacheIsFresh);

    if (cached) {
      setTripRows(cached.tripRows);
      setRuntimeRows(cached.runtimeRows);
      setPreviousTripRows(cached.previousTripRows);
      setPreviousRuntimeRows(cached.previousRuntimeRows);
      setComparisonReady(cached.comparisonReady !== false);
      setDashboardError("");
      setIsMetricsLoading(false);
    } else {
      setComparisonReady(false);
      setIsMetricsLoading(true);
      setDashboardError("");
    }

    const reportParams = {
      orgId: resolvedOrgId,
      category: "routes",
      selectedIds: [],
      selectAll: true,
      dateOption: dashboardDateOption,
      customStartDate: isCustom ? dashboardCustomRange.start : undefined,
      customEndDate: isCustom ? dashboardCustomRange.end : undefined,
    };
    const runtimeParams = {
      ...reportParams,
      category: "vehicles",
    };
    const comparisonPeriod = getComparisonPeriodParams(
      dashboardDateOption,
      dashboardCustomRange,
    );
    const comparisonParams = {
      ...reportParams,
      dateOption: comparisonPeriod.dateOption,
      customStartDate: comparisonPeriod.customStartDate,
      customEndDate: comparisonPeriod.customEndDate,
    };
    const previousRuntimeParams = {
      ...runtimeParams,
      dateOption: comparisonPeriod.dateOption,
      customStartDate: comparisonPeriod.customStartDate,
      customEndDate: comparisonPeriod.customEndDate,
    };
    const comparisonIsReady = Boolean(cached) && cached.comparisonReady !== false;
    const reuseComparison = comparisonIsReady && isComparisonSettled(comparisonPeriod);

    const applyCurrent = (entry) => {
      if (cancelled || !entry) return;
      setTripRows(entry.tripRows);
      setRuntimeRows(entry.runtimeRows);
      setPreviousTripRows(entry.previousTripRows || []);
      setPreviousRuntimeRows(entry.previousRuntimeRows || []);
      setComparisonReady(entry.comparisonReady !== false);
      setDashboardError("");
      setIsMetricsLoading(false);
    };

    const loadCurrentReports = (force = false) => {
      if (!force && !shouldRefetch && cached) return Promise.resolve(cached);
      const requestKey = `${cacheKey}|current`;
      return shareInflight(routeDashboardInflight, requestKey, async () => {
        const [tripReport, runtimeReport] = await Promise.all([
          reportsApi.getTripReport(reportParams),
          reportsApi.getRuntimeReport(runtimeParams),
        ]);
        const previousEntry = routeDashboardReportCache.get(cacheKey) || cached;
        const entry = {
          tripRows: getReportRows(tripReport),
          runtimeRows: getReportRows(runtimeReport),
          previousTripRows: previousEntry?.previousTripRows || [],
          previousRuntimeRows: previousEntry?.previousRuntimeRows || [],
          comparisonReady: Boolean(previousEntry?.comparisonReady) && reuseComparison,
          fetchedAt: Date.now(),
        };
        routeDashboardReportCache.set(cacheKey, entry);
        return entry;
      });
    };

    const loadComparisonReports = () => {
      const currentEntry = routeDashboardReportCache.get(cacheKey) || cached;
      if (currentEntry && currentEntry.comparisonReady !== false && (reuseComparison || !shouldRefetch)) {
        return Promise.resolve(currentEntry);
      }
      const requestKey = `${cacheKey}|comparison`;
      return shareInflight(routeDashboardInflight, requestKey, async () => {
        const [previousTripReport, previousRuntimeReport] = await Promise.all([
          reportsApi.getTripReport(comparisonParams),
          reportsApi.getRuntimeReport(previousRuntimeParams),
        ]);
        const latest = routeDashboardReportCache.get(cacheKey) || {};
        const entry = {
          ...latest,
          previousTripRows: getReportRows(previousTripReport),
          previousRuntimeRows: getReportRows(previousRuntimeReport),
          comparisonReady: true,
          fetchedAt: latest.fetchedAt || Date.now(),
        };
        routeDashboardReportCache.set(cacheKey, entry);
        return entry;
      });
    };

    const loadDeviationReport = (force = false) => {
      const deviationKey = routeDeviationCacheKey(
        resolvedOrgId,
        dashboardDateOption,
        dashboardCustomRange,
      );
      const cachedDeviation = routeDeviationCache.get(deviationKey) || null;
      const deviationFresh =
        cachedDeviation &&
        Date.now() - cachedDeviation.fetchedAt < ROUTE_DASHBOARD_REVALIDATE_MS;
      if (cachedDeviation) setDeviationRows(cachedDeviation.rows);
      if (!force && cachedDeviation && (!includesToday || deviationFresh)) {
        return Promise.resolve(cachedDeviation);
      }
      return shareInflight(routeDeviationInflight, deviationKey, async () => {
        const report = await reportsApi.getRouteDeviationReport({
          orgId: resolvedOrgId,
          category: "routes",
          selectedIds: [],
          selectAll: true,
          dateOption: dashboardDateOption,
          customStartDate: isCustom ? dashboardCustomRange.start : undefined,
          customEndDate: isCustom ? dashboardCustomRange.end : undefined,
        });
        const entry = {
          rows: getReportRows(report),
          fetchedAt: Date.now(),
        };
        routeDeviationCache.set(deviationKey, entry);
        return entry;
      }).then((entry) => {
        if (!cancelled && entry) setDeviationRows(entry.rows);
        return entry;
      }).catch((error) => {
        console.error("Failed to load route deviations:", error);
        if (!cancelled && !cachedDeviation) setDeviationRows([]);
        return null;
      });
    };

    loadCurrentReports()
      .then((entry) => {
        applyCurrent(entry);
        if (cancelled) return null;
        return Promise.allSettled([loadComparisonReports(), loadDeviationReport()]);
      })
      .then((results) => {
        if (cancelled || !results) return;
        const comparisonResult = results[0];
        if (comparisonResult.status === "fulfilled" && comparisonResult.value) {
          setPreviousTripRows(comparisonResult.value.previousTripRows || []);
          setPreviousRuntimeRows(comparisonResult.value.previousRuntimeRows || []);
          setComparisonReady(true);
        }
      })
      .catch((error) => {
        console.error("Failed to load route dashboard reports:", error);
        if (cancelled || cached) return;
        setTripRows([]);
        setRuntimeRows([]);
        setPreviousTripRows([]);
        setPreviousRuntimeRows([]);
        setComparisonReady(false);
        const backendErrorMessage =
          error?.response?.data?.error ||
          error?.response?.data?.message ||
          error?.message;
        if (backendErrorMessage) {
          setDashboardError(backendErrorMessage);
        }
        setIsMetricsLoading(false);
      });

    const refreshTimer = includesToday
      ? window.setInterval(() => {
          if (cancelled) return;
          loadCurrentReports(true)
            .then((entry) => {
              applyCurrent(entry);
              return loadDeviationReport(true);
            })
            .catch((error) => {
              console.error("Failed to refresh route dashboard reports:", error);
            });
        }, ROUTE_DASHBOARD_REVALIDATE_MS)
      : null;

    return () => {
      cancelled = true;
      if (refreshTimer) window.clearInterval(refreshTimer);
    };
  }, [
    orgId,
    dashboardDateOption,
    dashboardCustomRange.start,
    dashboardCustomRange.end,
  ]);

  const currentMetrics = useMemo(
    () =>
      buildRouteMetrics(routeData, tripRows, runtimeRows, deviationRows, {
        dateOption: dashboardDateOption,
        customRange: dashboardCustomRange,
      }),
    [
      dashboardCustomRange,
      dashboardDateOption,
      deviationRows,
      routeData,
      runtimeRows,
      tripRows,
    ],
  );
  const previousMetrics = useMemo(() => {
    const comparisonPeriod = getComparisonPeriodParams(
      dashboardDateOption,
      dashboardCustomRange,
    );
    return buildRouteMetrics(
      routeData,
      previousTripRows,
      previousRuntimeRows,
      deviationRows,
      {
        dateOption: comparisonPeriod.dateOption,
        customRange: {
          start: comparisonPeriod.customStartDate,
          end: comparisonPeriod.customEndDate,
        },
      },
    );
  }, [
    dashboardCustomRange,
    dashboardDateOption,
    deviationRows,
    previousRuntimeRows,
    previousTripRows,
    routeData,
  ]);
  const metricTrends = useMemo(
    () => comparisonReady ? ({
      activeRoutes: computePercentChangeTrend(
        currentMetrics.activeRoutes,
        previousMetrics.activeRoutes,
      ),
      tripsStarted: computePercentChangeTrend(
        currentMetrics.tripsStarted,
        previousMetrics.tripsStarted,
      ),
      delayedTrips: computePercentChangeTrend(
        currentMetrics.delayedTrips,
        previousMetrics.delayedTrips,
      ),
      avgDelay: computePercentChangeTrend(
        currentMetrics.avgDelayMinutes,
        previousMetrics.avgDelayMinutes,
      ),
      adherence: computePercentChangeTrend(
        currentMetrics.adherencePercent,
        previousMetrics.adherencePercent,
      ),
      totalRuntime: computePercentChangeTrend(
        currentMetrics.totalRuntime,
        previousMetrics.totalRuntime,
      ),
    }) : {},
    [comparisonReady, currentMetrics, previousMetrics],
  );

  const formatNumber = (value, digits = 0) =>
    Number(value || 0).toLocaleString(undefined, {
      maximumFractionDigits: digits,
    });

  const formatLayoutStatValue = (metricKey) => {
    switch (metricKey) {
      case "activeRoutes":
        return `${formatNumber(currentMetrics.activeRoutes)} / ${formatNumber(
          currentMetrics.totalRoutes || EMPTY_ROUTE_METRICS.totalRoutes,
        )}`;
      case "tripsStarted":
        return `${formatNumber(currentMetrics.tripsStarted)} / ${formatNumber(
          currentMetrics.tripsPlanned,
        )}`;
      case "delayedTrips":
        return formatNumber(currentMetrics.delayedTrips);
      case "avgDelay":
        return `${formatNumber(currentMetrics.avgDelayMinutes)} ${t("mins")}`;
      case "adherence":
        return currentMetrics.adherencePercent === null
          ? t("N/A")
          : `${currentMetrics.adherencePercent}%`;
      case "totalRuntime":
        return `${formatNumber(currentMetrics.totalRuntime, 2)} ${t("Hrs")}`;
      case "totalRoutes":
        return formatNumber(currentMetrics.totalRoutes || routeData.length);
      case "connectedStops":
        return formatNumber(computeFleetRouteMetric("connectedStops", routeData));
      default:
        return formatNumber(computeFleetRouteMetric(metricKey, routeData));
    }
  };

  const getLayoutStatTrend = (metricKey) => metricTrends[metricKey] || {
    trend: undefined,
    trendDirection: "none",
  };

  const fallbackRouteStatsCards = [
    {
      title: "Active Routes",
      value: formatLayoutStatValue("activeRoutes"),
      icon: AltRouteIcon,
      tone: "blue",
      trend: metricTrends.activeRoutes?.trend,
      trendDirection: metricTrends.activeRoutes?.trendDirection,
    },
    {
      title: "Trips Started",
      value: formatLayoutStatValue("tripsStarted"),
      icon: PlayCircleOutlineIcon,
      tone: "amber",
      trend: metricTrends.tripsStarted?.trend,
      trendDirection: metricTrends.tripsStarted?.trendDirection,
    },
    {
      title: "Delayed Trips",
      value: formatLayoutStatValue("delayedTrips"),
      icon: WarningAmberIcon,
      tone: "red",
      trend: metricTrends.delayedTrips?.trend,
      trendDirection: metricTrends.delayedTrips?.trendDirection,
    },
    {
      title: "Avg. Delay",
      value: formatLayoutStatValue("avgDelay"),
      icon: AccessTimeIcon,
      tone: "purple",
      trend: metricTrends.avgDelay?.trend,
      trendDirection: metricTrends.avgDelay?.trendDirection,
    },
    {
      title: "Avg. Route Adherence",
      value: formatLayoutStatValue("adherence"),
      icon: CheckCircleOutlineIcon,
      tone: "green",
      trend: metricTrends.adherence?.trend,
      trendDirection: metricTrends.adherence?.trendDirection,
    },
    {
      title: "Total Runtime",
      value: formatLayoutStatValue("totalRuntime"),
      icon: SpeedIcon,
      tone: "cyan",
      trend: metricTrends.totalRuntime?.trend,
      trendDirection: metricTrends.totalRuntime?.trendDirection,
    },
  ];

  const STAT_ICON_BY_METRIC = {
    activeRoutes: AltRouteIcon,
    tripsStarted: PlayCircleOutlineIcon,
    delayedTrips: WarningAmberIcon,
    avgDelay: AccessTimeIcon,
    adherence: CheckCircleOutlineIcon,
    totalRuntime: SpeedIcon,
    totalRoutes: AltRouteIcon,
    connectedStops: AltRouteIcon,
  };

  const loadedSummaryWidgets = getLayoutSection(moduleLayout, "summaryStats")?.widgets;
  const bundledSummaryWidgets = getLayoutSection(bundledLayout, "summaryStats")?.widgets;
  const hasNewDashboardStats = loadedSummaryWidgets?.some((widget) =>
    ROUTE_DASHBOARD_STAT_METRIC_KEYS.includes(widget.metricKey),
  );
  const layoutSummaryWidgets = hasNewDashboardStats
    ? loadedSummaryWidgets
    : bundledSummaryWidgets || loadedSummaryWidgets;
  const routeStatsCards = layoutSummaryWidgets?.length
    ? layoutSummaryWidgets.map((widget) => {
        const trend = getLayoutStatTrend(widget.metricKey);
        return {
          title: widget.labelKey,
          value: formatLayoutStatValue(widget.metricKey),
          icon: STAT_ICON_BY_METRIC[widget.metricKey] || AltRouteIcon,
          tone:
            widget.tone ||
            ROUTE_STAT_TONE_BY_VARIANT[widget.variant] ||
            "blue",
          trend: trend?.trend,
          trendDirection: trend?.trendDirection,
        };
      })
    : fallbackRouteStatsCards;

  const handleAddRoute = () => {
    if (!canCreateRoute) return;
    setExpandedRouteId(null);
    setShowCreateForm((isOpen) => !isOpen);
  };

  const handleCreateRouteComplete = () => {
    setShowCreateForm(false);
    fetchRoutes(true);
  };

  const toggleExpandedRouteRow = (routeId) => {
    if (!canUpdateRoute) return;
    setShowCreateForm(false);
    setExpandedRouteId((currentId) =>
      String(currentId || "") === String(routeId || "") ? null : routeId,
    );
  };

  const handleUpdateRouteComplete = () => {
    setExpandedRouteId(null);
    fetchRoutes(true);
  };

  const handleViewAllRoutesOnMap = () =>
    navigate(isSchool ? "/route/allroutes?app=school" : "/route/allroutes");

  const handleDeleteCancel = () => {
    setIsDeleteModalOpen(false);
    setRouteToDelete(null);
  };

  const handleDeleteConfirm = async () => {
    if (routeToDelete) {
      try {
        await axios.delete(`${config.baseURL}/route`, {
          data: { id: Number(routeToDelete.id), organizationId: orgId },
          withCredentials: true,
        });
        fetchRoutes(true);
        setExpandedRouteId(null);
      } catch (error) {
        console.log("Error in deleting route: ", error);
      }
    }
    setIsDeleteModalOpen(false);
    setRouteToDelete(null);
  };

  const handleRouteAction = (actionId, route, actionObj = {}) => {
    const routeTrackingState = {
      id: route.id,
      name: route.name,
      routeId: route.routeId,
      stops: route.stops || [],
      vehicles: route.vehicles || [],
      routeCoordinates: route.routeCoordinates || [],
      schedules: route.schedules || [],
      routeColor: route.routeColor || "#000000",
    };

    switch (actionId) {
      case "update":
        toggleExpandedRouteRow(route.id);
        break;
      case "analytics":
        if (isSchool) {
          handleRouteAction("monitor", route);
          break;
        }
        navigate(`/route/track#analytics`, {
          state: {
            selectedRouteData: routeTrackingState,
            openSection: "analytics",
          },
        });
        break;
      case "monitor":
        if (isSchool) {
          localStorage.setItem(
            `schoolRouteMonitor:${route.id}`,
            JSON.stringify(route),
          );
          window.open(
            `/school-routes/monitor/${route.id}`,
            "_blank",
            "noopener,noreferrer",
          );
          break;
        }
        navigate(`/route/track`, {
          state: {
            selectedRouteData: routeTrackingState,
            openSection: "top",
          },
        });
        break;
      case "schedule":
        navigate(
          actionObj.path || (isSchool ? "/school-schedules" : "/route/schedules"),
          { state: { id: route.id } },
        );
        break;
      case "delete":
        setRouteToDelete(route);
        setIsDeleteModalOpen(true);
        break;
      default:
        break;
    }
  };

  const tableRows = useMemo(
    () =>
      routeData.map((route) => {
        const status = getRouteOperationalStatus(route, tripRows, deviationRows);
        const adherence = getRouteAdherencePercent(route, tripRows, deviationRows);
        const corridor = getStopCorridor(route);
        const vehicleNames = getVehicleNames(route.vehicles);
        return {
          id: route.id,
          routeCode: route.routeId || t("N/A"),
          routeName: route.name || t("N/A"),
          stopCorridor: corridor === "No stops" ? t("No stops") : corridor,
          stopCount: getRouteStopCount(route),
          distanceLabel: formatDistanceLabel(getRouteDistanceKm(route)),
          durationLabel: formatDurationLabel(
            getRouteDurationMinutes(route, tripRows),
            t,
          ),
          status: status.label,
          statusTone: status.tone,
          adherenceLabel: adherence === null ? "--" : `${adherence}%`,
          lastUpdate: formatLastUpdate(getRouteLastUpdateValue(route, tripRows), t),
          lastUpdateTone: status.tone === "green" ? "green" : "amber",
          vehicles: vehicleNames === "No vehicles" ? t("No vehicles") : vehicleNames,
          originalData: route,
        };
      }),
    [deviationRows, routeData, t, tripRows],
  );

  const filteredTableRows = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    return tableRows
      .filter((row) => {
        const matchesSearch =
          !query ||
          [
            row.routeCode,
            row.routeName,
            row.stopCorridor,
            row.stopCount,
            row.distanceLabel,
            row.durationLabel,
            row.status,
            t(row.status),
            row.adherenceLabel,
            row.lastUpdate,
            row.vehicles,
          ].some((value) => String(value ?? "").toLowerCase().includes(query));
        const matchesStatus = !statusFilter || row.status === statusFilter;
        const matchesRoute =
          !routeFilter || String(row.id) === String(routeFilter);
        const matchesQuickFilter =
          quickFilter !== "attention" ||
          row.status === "Delayed" ||
          row.status === "Deviated";
        return matchesSearch && matchesStatus && matchesRoute && matchesQuickFilter;
      })
      .sort((a, b) => {
        const newestFirst = sortBy === "Newest First";
        const getCreatedTime = (row) => {
          const route = row.originalData || row;
          const value =
            route.createdAt ||
            route.created_at ||
            route.createdDate ||
            route.created_date;
          const time = value ? new Date(value).getTime() : Number.NaN;
          return Number.isFinite(time) ? time : 0;
        };
        const timeDifference = getCreatedTime(a) - getCreatedTime(b);
        if (timeDifference !== 0) {
          return newestFirst ? -timeDifference : timeDifference;
        }
        const idDifference = Number(a.id || 0) - Number(b.id || 0);
        return newestFirst ? -idDifference : idDifference;
      });
  }, [quickFilter, routeFilter, searchQuery, sortBy, statusFilter, t, tableRows]);

  const showDelayedRoutesInTable = () => {
    setQuickFilter("attention");
    setSearchQuery("");
    setStatusFilter("");
    setRouteFilter("");
    window.setTimeout(() => {
      tableSectionRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 100);
  };

  const clearQuickFilter = () => setQuickFilter("");

  const routeOptions = useMemo(
    () =>
      routeData.map((route) => ({
        value: String(route.id),
        label: route.name || route.routeId || `Route ${route.id}`,
      })),
    [routeData],
  );
  const statusOptions = useMemo(
    () =>
      Array.from(new Set(tableRows.map((row) => row.status).filter(Boolean))).map(
        (status) => ({ value: status, label: status }),
      ),
    [tableRows],
  );
  const headers = useMemo(() => [
    { key: "routeCode", label: "Route ID", width: "110px" },
    {
      key: "routeName",
      label: "Route Name",
      width: "180px",
      render: (row) => (
        <div className="vehicle-new-driver-cell">
          <strong>{row.routeName}</strong>
          <span>{row.stopCorridor}</span>
        </div>
      ),
    },
    { key: "stopCount", label: "Stops", width: "80px" },
    { key: "distanceLabel", label: "Distance", width: "100px" },
    { key: "durationLabel", label: "Avg. Duration", width: "120px" },
    {
      key: "status",
      label: "Status",
      width: "120px",
      render: (row) => (
        <TableStatusBadge tone={row.statusTone}>{t(row.status)}</TableStatusBadge>
      ),
    },
    { key: "adherenceLabel", label: "Route Adherence", width: "140px" },
    {
      key: "lastUpdate",
      label: "Last Update",
      width: "130px",
      render: (row) => (
        <TableLastUpdate tone={row.lastUpdateTone}>{row.lastUpdate}</TableLastUpdate>
      ),
    },
    { key: "vehicles", label: "Vehicles", width: "180px" },
  ], [t]);

  const tableFilters = useMemo(() => [
    {
      key: "route",
      label: "All Routes",
      value: routeFilter,
      options: [{ value: "", label: "All Routes" }, ...routeOptions],
      onChange: setRouteFilter,
    },
    {
      key: "status",
      label: "All Status",
      value: statusFilter,
      options: [{ value: "", label: "All Status" }, ...statusOptions],
      onChange: setStatusFilter,
    },
    {
      key: "sortBy",
      label: "Newest First",
      value: sortBy,
      options: [
        { value: "Newest First", label: "Newest First" },
        { value: "Oldest First", label: "Oldest First" },
      ],
      onChange: setSortBy,
    },
  ], [routeFilter, routeOptions, sortBy, statusFilter, statusOptions]);

  const displayedTableActions =
    canMonitorRoute || isSchool
      ? [
          {
            id: "analytics",
            label: "Analytics",
            icon: BarChartOutlined,
            onClick: (row) => handleRouteAction("analytics", row.originalData),
          },
          {
            id: "monitor",
            label: "Monitor",
            icon: VisibilityOutlinedIcon,
            onClick: (row) => handleRouteAction("monitor", row.originalData),
          },
        ]
      : tableActions.map((action) => ({
          ...action,
          onClick: (row) => handleRouteAction(action.id, row.originalData, action),
        }));

  const userState = localStorage.getItem("userState");
  const parsedUserState = userState ? JSON.parse(userState) : null;
  const resolvedOrgId = parsedUserState?.user?.organizationId || getOrganizationId() || orgId;
  const cleanOrgName = (parsedUserState?.organization?.name || orgName || "RouteEye")
    .replace(/[^a-zA-Z0-9_-]/g, "_");
  const routeExportFileName = `${cleanOrgName}_${isSchool ? "School_Routes" : "Routes"}`;

  const handleDownloadRoutes = async () => {
    const downloadFileName = `${routeExportFileName}.xlsx`;

    const downloadParams = {
      organizationId: resolvedOrgId,
      table: "routes",
      format: "xlsx",
      includeDetails: true,
      search: searchQuery || undefined,
      status: statusFilter || undefined,
    };

    if (selectedRouteIds && selectedRouteIds.length > 0) {
      downloadParams.routeIds = selectedRouteIds.join(",");
      downloadParams.ids = selectedRouteIds.join(",");
    }

    try {
      // 1. Primary backend route: /excel/download?table=routes&organizationId=...&format=xlsx
      await downloadBackendExcel({
        url: `${config.baseURL}/excel/download`,
        params: downloadParams,
        defaultFileName: downloadFileName,
      });
    } catch (err) {
      console.warn("Backend /excel/download table=routes failed, trying table=route:", err);
      try {
        // 2. Singular fallback: table=route
        await downloadBackendExcel({
          url: `${config.baseURL}/excel/download`,
          params: {
            ...downloadParams,
            table: "route",
          },
          defaultFileName: downloadFileName,
        });
      } catch (err2) {
        console.warn("Backend /excel/download table=route failed, trying /excel/download/routes:", err2);
        try {
          // 3. Path parameter fallback: /excel/download/routes
          await downloadBackendExcel({
            url: `${config.baseURL}/excel/download/routes`,
            params: downloadParams,
            defaultFileName: downloadFileName,
          });
        } catch (fallbackErr) {
          console.warn("Backend export not available yet, using client export fallback:", fallbackErr);
          const targetData = selectedRouteIds.length > 0
            ? filteredTableRows.filter((row) => selectedRouteIds.some((id) => String(id) === String(row.id)))
            : filteredTableRows;

          const exportHeaders = headers.filter((h) => h.key !== "actions" && h.key !== "Actions");

          exportTableToCSV(
            routeExportFileName,
            exportHeaders,
            targetData,
          );
        }
      }
    }
  };

  return (
    <>
      <SubTitleNew
        title={pageTitle}
        path={`${orgName || "organization"} > Routes`}
        add={
          !organizationInactive && canCreateRoute
            ? createAction?.labelKey || "Create Route"
            : undefined
        }
        onAddClick={
          !organizationInactive && canCreateRoute ? handleAddRoute : undefined
        }
        track={
          !organizationInactive
            ? monitorAction?.labelKey || "Monitor Routes"
            : undefined
        }
        onTrackClick={
          !organizationInactive ? handleViewAllRoutesOnMap : undefined
        }
        showDashboardControls={!organizationInactive}
        dateOptions={!organizationInactive ? DASHBOARD_DATE_OPTIONS : undefined}
        dateError={dashboardError}
        onDateRangeChange={
          !organizationInactive ? handleDashboardDateRangeChange : undefined
        }
      />
      <div className="vehicle-new-page-body">
        {canCreateRoute && showCreateForm && (
          <div className="vehicle-new-create-panel">
            <RouteAddMain
              embedded
              onCancel={() => setShowCreateForm(false)}
              onComplete={handleCreateRouteComplete}
            />
          </div>
        )}
        {organizationInactive ? (
          <InactiveInventoryTable
            title="routeInventory"
            headers={headers}
            data={tableRows}
            loading={isLoading}
            emptyMessage={t("noDataFound")}
            searchPlaceholder={t("Search routes")}
            itemLabel="routes"
            minWidth={1180}
          />
        ) : (
          <>
        {dashboardError && (
          <Box sx={{ mb: 2 }}>
            <Alert severity="error" onClose={() => setDashboardError("")}>
              {t(dashboardError)}
            </Alert>
          </Box>
        )}
        <div className="stats-card-grid vehicle-new-stats-grid">
          {routeStatsCards.map((card) => (
            <StatsCard
              key={card.title}
              title={card.title}
              value={card.value}
              icon={card.icon}
              tone={card.tone}
              trend={card.trend}
              trendDirection={card.trendDirection}
              loading={isMetricsLoading}
            />
          ))}
        </div>

        <RouteDashboardGraph
          routes={routeData}
          tripRows={tripRows}
          deviationRows={deviationRows}
          error={dashboardError}
          onViewDelayedRoutes={showDelayedRoutesInTable}
          dateOption={dashboardDateOption}
          customStartDate={dashboardCustomRange.start}
          customEndDate={dashboardCustomRange.end}
          organizationId={orgId}
        />

        <div
          ref={tableSectionRef}
          className="table_container vehicle-new-table-container"
        >
          <DynamicTable
            title={`${t("All Routes")} (${filteredTableRows.length} ${filteredTableRows.length === 1 ? t("Route") : t("Routes")})`}
            className="vehicle-new-fleet-table"
            headers={headers}
            data={filteredTableRows}
            downloadData={filteredTableRows}
            downloadFileName={routeExportFileName}
            selectable={true}
            selectedRowKeys={selectedRouteIds}
            onSelectionChange={setSelectedRouteIds}
            onDownload={handleDownloadRoutes}
            searchValue={searchQuery}
            onSearchChange={setSearchQuery}
            searchPlaceholder="Search routes"
            filters={tableFilters}
            toolbarStart={
              quickFilter === "attention" ? (
                <span className="new-dynamic-table-filter-chip">
                  {t("Delayed / Deviated")}
                  <button
                    type="button"
                    aria-label={t("Clear route filter")}
                    onClick={clearQuickFilter}
                  >
                    ×
                  </button>
                </span>
              ) : null
            }
            actions={displayedTableActions}
            loading={isLoading}
            emptyMessage="No routes found"
            headerVariant="light"
            minWidth={1180}
            rowKey={(row) => row.id}
            onRowClick={
              canUpdateRoute
                ? (row) => toggleExpandedRouteRow(row.id)
                : undefined
            }
            renderExpandedRow={
              canUpdateRoute
                ? (row) => {
                    if (String(expandedRouteId || "") !== String(row.id || "")) {
                      return null;
                    }

                    return (
                      <div className="vehicle-new-expanded-form">
                        <RouteAddMain
                          key={row.id}
                          embedded
                          embeddedRoute={row.originalData}
                          onCancel={() => setExpandedRouteId(null)}
                          onComplete={handleUpdateRouteComplete}
                          onDelete={
                            hasPermission("/api/route", "DELETE")
                              ? () => handleRouteAction("delete", row.originalData)
                              : undefined
                          }
                        />
                      </div>
                    );
                  }
                : undefined
            }
            pagination={{
              enabled: false,
              itemLabel: "routes",
            }}
          />
        </div>
          </>
        )}
      </div>
      <DeleteModal
        isOpen={isDeleteModalOpen}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title="Delete Route!"
        message={
          <>
            {t("Are you sure you want to delete")}{" "}
            <span style={{ fontWeight: 600, color: "#d32f2f" }}>
              {routeToDelete?.name || t("this route")}
            </span>
            ? {t("This action cannot be undone.")}
          </>
        }
      />
    </>
  );
};

export default Route;
